import React from 'react';
import ReactTable from 'react-table';
import { config } from 'aws-cognito-redux-saga';
import LeftBar from '../Site/LeftBar';
import CustomerBar from '../Site/CustomerBar';

export async function oversizeRequest(path = '', method = 'GET', body) {
  const cognito = config.config || config;
  const user = cognito.getUserPool() && cognito.getUser();
  if (!user) throw new Error('Sign in to manage Apex oversize products.');
  const session = await new Promise((resolve, reject) => {
    user.getSession((error, result) => error ? reject(error) : resolve(result));
  });
  const response = await fetch(process.env.REACT_APP_NODE_ROOT_URL + '/apex-oversize' + path, {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + session.getIdToken().getJwtToken()
    },
    cache: 'no-store',
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Unable to update the Apex oversize list.');
  return data;
}

export default function ApexOversize({ location }) {
  const customer = location.pathname.startsWith('/customer');
  const allowed = ['2', '23'].includes(localStorage.getItem('customerClientId'));
  const [entries, setEntries] = React.useState([]);
  const [upc, setUpc] = React.useState('');
  const [sku, setSku] = React.useState('');
  const [originalUpc, setOriginalUpc] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');

  React.useEffect(() => {
    if (!allowed) return;
    let active = true;
    setBusy(true);
    oversizeRequest().then(rows => {
      if (active) setEntries(rows);
    }).catch(error => {
      console.error('Loading Apex oversize list failed:', error);
      if (active) setError(error.message);
    }).finally(() => {
      if (active) setBusy(false);
    });
    return () => { active = false; };
  }, [allowed]);

  function resetForm() {
    setUpc('');
    setSku('');
    setOriginalUpc(null);
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const saved = await oversizeRequest(originalUpc === null ? '' : '/' + encodeURIComponent(originalUpc),
        originalUpc === null ? 'POST' : 'PUT', { pr_upc: upc, pr_sku: sku });
      setEntries(rows => rows.filter(row => row.pr_upc !== originalUpc).concat(saved));
      resetForm();
      setMessage('Oversize entry saved. Reopen or refresh an order to see updated packing alerts.');
    } catch (error) {
      console.error('Saving Apex oversize entry failed:', error);
      setError(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove(entry) {
    if (!window.confirm('Remove UPC ' + entry.pr_upc + ' from the Apex oversize list?')) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await oversizeRequest('/' + encodeURIComponent(entry.pr_upc), 'DELETE');
      setEntries(rows => rows.filter(row => row.pr_upc !== entry.pr_upc));
      if (originalUpc === entry.pr_upc) resetForm();
      setMessage('Oversize entry removed. Reopen or refresh an order to see updated packing alerts.');
    } catch (error) {
      console.error('Removing Apex oversize entry failed:', error);
      setError(error.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {customer ? <CustomerBar /> : <LeftBar />}
      <div className="page-wrapper">
        <div className="row page-titles">
          <div className="col-md-12 align-self-center">
            <h3 className="text-primary">Apex Oversize Packing</h3>
          </div>
        </div>
        <div className="container-fluid">
          <div className="card"><div className="card-body">
            {!allowed ? <div role="alert" className="alert alert-danger">Only Apex customers and staff can manage this list.</div> : (
              <React.Fragment>
                <p>For Apex, an order item with a UPC in this list must have each unit packed in its own box.s</p>
                {error && <div role="alert" className="alert alert-danger">{error}</div>}
                {message && <div role="status" className="alert alert-success">{message}</div>}
                <form className="mb-4" onSubmit={save}>
                  <fieldset disabled={busy}>
                    <legend>{originalUpc === null ? 'Add oversize entry' : 'Edit oversize entry'}</legend>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label htmlFor="oversize-upc">UPC</label>
                          <input id="oversize-upc" className="form-control form-control-sm" type="text" required maxLength={100}
                            value={upc} onChange={event => setUpc(event.target.value)} />
                        </div>
                        <div className="form-group">
                          <label htmlFor="oversize-sku">SKU</label>
                          <input id="oversize-sku" className="form-control form-control-sm" type="text" required maxLength={100}
                            value={sku} onChange={event => setSku(event.target.value)} />
                        </div>
                      </div>
                    </div>
                    <button className="btn btn-primary btn-sm mr-2" type="submit">{originalUpc === null ? 'Add entry' : 'Save changes'}</button>
                    {originalUpc !== null && <button className="btn btn-secondary btn-sm" type="button" onClick={resetForm}>Cancel edit</button>}
                  </fieldset>
                </form>
                <ReactTable data={entries} loading={busy} filterable defaultPageSize={20}
                  defaultSorted={[{ id: 'pr_upc', desc: false }]}
                  defaultFilterMethod={(filter, row) => String(row[filter.id] || '').toLowerCase().includes(filter.value.toLowerCase())}
                  noDataText="No Apex oversize entries"
                  columns={[
                    { Header: 'UPC', accessor: 'pr_upc' },
                    { Header: 'SKU', accessor: 'pr_sku' },
                    { Header: 'Actions', id: 'actions', filterable: false, sortable: false, Cell: row => (
                      <div>
                        <button type="button" className="btn btn-secondary btn-sm mr-2" disabled={busy} onClick={() => {
                          setOriginalUpc(row.original.pr_upc);
                          setUpc(row.original.pr_upc);
                          setSku(row.original.pr_sku || '');
                          setMessage('');
                          setError('');
                        }}>Edit</button>
                        <button type="button" className="btn btn-danger btn-sm" disabled={busy}
                          onClick={() => remove(row.original)}>Remove</button>
                      </div>
                    ) }
                  ]} />
              </React.Fragment>
            )}
          </div></div>
        </div>
      </div>
    </div>
  );
}
